import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Button,
  FormGrid,
  FormGroup,
  FormSection,
  Input,
  Label,
  SectionTitle,
  TextArea,
} from "../styles";
import { Profile } from "../types";
import { FieldSyncBadge } from "./FieldSyncBadge";
import {
  GabaritoState,
  initialGabaritoState,
  removeKeyword,
  renameFileWithRandomKeyword,
  splitKeywords,
} from "./gabarito-utils";
import { getApiErrorMessage, profilesTrackingService } from "../../../services";

const HelperText = styled.p`
  margin: -4px 0 16px 0;
  color: #5f6368;
  font-size: 14px;
  line-height: 1.5;
`;

const UploadBox = styled.div`
  min-height: 220px;
  border: 2px dashed #d7dde8;
  border-radius: 16px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  margin-bottom: 24px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #80868b;
  font-size: 14px;

  .material-icons {
    font-size: 42px;
    color: #9aa0a6;
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const ImageCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  overflow: hidden;
  background: #ffffff;
`;

const ImagePreview = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  background: #f1f3f4;
`;

const ImageMeta = styled.div`
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const KeywordList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const KeywordChip = styled.span<{ $green?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 7px 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => (props.$green ? "#137333" : "#1967d2")};
  background: ${(props) => (props.$green ? "#e6f4ea" : "#e8f0fe")};
`;

const KeywordRemoveButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  padding: 0;

  .material-icons {
    font-size: 15px;
  }
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
`;

const HiddenInput = styled.input`
  display: none;
`;

type GabaritoTabProps = {
  profile: Profile | null;
};

export const GabaritoTab: React.FC<GabaritoTabProps> = ({ profile }) => {
  const profileId = profile?.id;
  const [gabarito, setGabarito] = useState<GabaritoState>(initialGabaritoState);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    if (!profileId) return;
    setFeedback("");
    let active = true;
    (async () => {
      try {
        const data = await profilesTrackingService.getGabarito(profileId);
        if (!active) return;
        if (data) {
          setGabarito({
            postKeywords: data.postKeywords ?? "",
            commentKeywords: data.commentKeywords ?? "",
            postNotes: data.postNotes ?? "",
            commentNotes: data.commentNotes ?? "",
            images: Array.isArray(data.images)
              ? data.images.map((img: any) => ({
                  id: String(img.id),
                  title: img.keyword ?? img.fileName ?? "",
                  fileName: img.fileName ?? "",
                  originalFileName: img.fileName ?? "",
                  keyword: img.keyword ?? "",
                  src: img.url,
                }))
              : [],
          });
        } else {
          setGabarito(initialGabaritoState);
        }
      } catch (error) {
        console.error("Erro ao carregar gabarito:", error);
        if (active) setGabarito(initialGabaritoState);
      }
    })();
    return () => {
      active = false;
    };
  }, [profileId]);

  const postKeywords = useMemo(
    () => splitKeywords(gabarito.postKeywords),
    [gabarito.postKeywords],
  );

  const commentKeywords = useMemo(
    () => splitKeywords(gabarito.commentKeywords),
    [gabarito.commentKeywords],
  );

  const updateField = (field: keyof GabaritoState, value: string) => {
    setGabarito((prev) => ({ ...prev, [field]: value }));
  };

  const handleRemoveKeyword = (
    field: "postKeywords" | "commentKeywords",
    keyword: string,
  ) => {
    setGabarito((prev) => ({
      ...prev,
      [field]: removeKeyword(prev[field], keyword),
    }));
  };

  const handleSave = async () => {
    if (!profileId) return;
    try {
      await profilesTrackingService.saveGabaritoText(profileId, {
        postKeywords: gabarito.postKeywords,
        commentKeywords: gabarito.commentKeywords,
        postNotes: gabarito.postNotes,
        commentNotes: gabarito.commentNotes,
      });
      setFeedback("Gabarito salvo para este perfil.");
    } catch (error) {
      console.error("Erro ao salvar gabarito:", error);
      setFeedback(
        `Erro ao salvar Gabarito: ${getApiErrorMessage(
          error,
          "motivo nao identificado.",
        )}`,
      );
    }
  };

  const handleRemoveImage = async (id: string) => {
    if (profileId) {
      try {
        await profilesTrackingService.deleteGabaritoImage(profileId, id);
      } catch (error) {
        console.error("Erro ao remover imagem do gabarito:", error);
      }
    }
    setGabarito((prev) => ({
      ...prev,
      images: prev.images.filter((image) => image.id !== id),
    }));
  };

  const handleImageUpload = (files: FileList | null) => {
    const file = files?.[0];
    if (!file || !profileId) return;

    const { file: renamedFile, keyword } = renameFileWithRandomKeyword(
      file,
      postKeywords.length > 0 ? postKeywords : [profile?.name ?? "imagem"],
    );

    const reader = new FileReader();
    reader.onload = async () => {
      const src = String(reader.result);
      try {
        const saved = await profilesTrackingService.addGabaritoImage(
          profileId,
          {
            keyword,
            fileName: renamedFile.name,
            src,
          },
        );
        setGabarito((prev) => ({
          ...prev,
          images: [
            ...prev.images,
            {
              id: String(saved?.id ?? `${renamedFile.name}-${Date.now()}`),
              title: keyword,
              fileName: renamedFile.name,
              originalFileName: file.name,
              keyword,
              src: saved?.url ?? src,
            },
          ],
        }));
        setFeedback(`Imagem ${renamedFile.name} adicionada ao gabarito.`);
      } catch (error) {
        console.error("Erro ao adicionar imagem do gabarito:", error);
        setFeedback("Nao foi possivel adicionar a imagem. Tente novamente.");
      }
    };
    reader.readAsDataURL(renamedFile);
  };

  return (
    <FormSection>
      <ActionRow>
        <div>
          <SectionTitle>
            Gabarito do Perfil
            <FieldSyncBadge kind="backend" />
          </SectionTitle>
          <HelperText>
            O Gabarito alimenta keywords internas, sugestoes da aba Postagens e
            rotinas agendadas futuras. Salvar aqui nao publica automaticamente
            uma postagem no Google; a publicacao manual acontece na aba Postagens.
          </HelperText>
        </div>
        <Button type="button" onClick={handleSave}>
          <span className="material-icons">save</span>
          <span>Salvar gabarito</span>
        </Button>
      </ActionRow>

      {feedback && <HelperText>{feedback}</HelperText>}

      <FormGroup>
        <ActionRow>
          <Label>
            <span className="material-icons">image</span>
            Fotos das postagens
            <FieldSyncBadge kind="backend" />
          </Label>
          <Button as="label" type="button" $secondary>
            <span className="material-icons">add_photo_alternate</span>
            <span>Adicionar foto</span>
            <HiddenInput
              type="file"
              accept="image/*"
              onChange={(event) => {
                handleImageUpload(event.target.files);
                event.target.value = "";
              }}
            />
          </Button>
        </ActionRow>

        {gabarito.images.length === 0 ? (
          <UploadBox>
            <EmptyState>
              <span className="material-icons">image</span>
              <span>Nenhuma foto cadastrada para este perfil</span>
            </EmptyState>
          </UploadBox>
        ) : (
          <ImageGrid>
            {gabarito.images.map((image) => (
              <ImageCard key={image.id}>
                <ImagePreview src={image.src} alt={image.title} />
                <ImageMeta>
                  <Input
                    value={image.title}
                    onChange={(event) =>
                      setGabarito((prev) => ({
                        ...prev,
                        images: prev.images.map((item) =>
                          item.id === image.id
                            ? { ...item, title: event.target.value }
                            : item,
                        ),
                      }))
                    }
                  />
                  <HelperText>
                    Arquivo: {image.fileName}
                    <br />
                    Original: {image.originalFileName}
                  </HelperText>
                  <Button
                    type="button"
                    $secondary
                    onClick={() => handleRemoveImage(image.id)}
                  >
                    <span className="material-icons">delete</span>
                    <span>Remover</span>
                  </Button>
                </ImageMeta>
              </ImageCard>
            ))}
          </ImageGrid>
        )}
      </FormGroup>

      <FormGrid>
        <FormGroup>
          <Label htmlFor="gabarito-post-keywords">
            <span className="material-icons">sell</span>
            Palavras-chave das postagens
            <FieldSyncBadge kind="backend" />
          </Label>
          <TextArea
            id="gabarito-post-keywords"
            value={gabarito.postKeywords}
            onChange={(event) => updateField("postKeywords", event.target.value)}
            placeholder="Ex: segurança eletrônica, alarmes, monitoramento"
          />
          <KeywordList>
            {postKeywords.map((keyword) => (
              <KeywordChip key={keyword}>
                {keyword}
                <KeywordRemoveButton
                  type="button"
                  onClick={() => handleRemoveKeyword("postKeywords", keyword)}
                >
                  <span className="material-icons">close</span>
                </KeywordRemoveButton>
              </KeywordChip>
            ))}
          </KeywordList>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="gabarito-comment-keywords">
            <span className="material-icons">forum</span>
            <FieldSyncBadge kind="backend" />
            Palavras para respostas de comentários
          </Label>
          <TextArea
            id="gabarito-comment-keywords"
            value={gabarito.commentKeywords}
            onChange={(event) =>
              updateField("commentKeywords", event.target.value)
            }
            placeholder="Ex: obrigado, atendimento, experiência"
          />
          <KeywordList>
            {commentKeywords.map((keyword) => (
              <KeywordChip key={keyword} $green>
                {keyword}
                <KeywordRemoveButton
                  type="button"
                  onClick={() =>
                    handleRemoveKeyword("commentKeywords", keyword)
                  }
                >
                  <span className="material-icons">close</span>
                </KeywordRemoveButton>
              </KeywordChip>
            ))}
          </KeywordList>
        </FormGroup>
      </FormGrid>

      <FormGrid>
        <FormGroup>
          <Label htmlFor="gabarito-post-notes">
            <span className="material-icons">notes</span>
            <FieldSyncBadge kind="backend" />
            Observações para postagens
          </Label>
          <TextArea
            id="gabarito-post-notes"
            value={gabarito.postNotes}
            onChange={(event) => updateField("postNotes", event.target.value)}
            placeholder="Preferências, termos obrigatórios e assuntos que devem aparecer."
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="gabarito-comment-notes">
            <span className="material-icons">quickreply</span>
            <FieldSyncBadge kind="backend" />
            Observações para respostas
          </Label>
          <TextArea
            id="gabarito-comment-notes"
            value={gabarito.commentNotes}
            onChange={(event) => updateField("commentNotes", event.target.value)}
            placeholder="Tom de voz, palavras proibidas e instruções para responder comentários."
          />
        </FormGroup>
      </FormGrid>
    </FormSection>
  );
};
